import { useEffect, useRef, useState } from 'react';
import { doc, getDoc as nativeGetDoc, setDoc as nativeSetDoc } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import TextInput from '../formComponents/TextInput';
import '../../teams/qualifications/qualifications.css';
const DEFAULT_FIELD = {
  id: '',
  label: '',
  type: 'text',
  required: false,
  options: []
};
const FIELD_TYPES = ['text', 'number', 'date', 'select', 'qualification'];
export default function EditProfileFieldsForm({
  teamId,
  isOpen,
  onClose,
  onSave,
  dataAccess = null,
}) {
  const { getDoc, setDoc } = dataAccess || { getDoc: nativeGetDoc, setDoc: nativeSetDoc };
  const [enabled, setEnabled] = useState(true);
  const [fields, setFields] = useState([]);
  const [joinQuestions, setJoinQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const initialFieldIdsRef = useRef(new Set());
  const confirmedJoinRemovalIdsRef = useRef(new Set());
  useEffect(() => {
    if (!isOpen) return;
    async function fetchConfig() {
      setLoading(true);
      setError('');
      try {
        const profileRef = doc(db, 'teams', teamId, 'profileFields', 'config');
        const joinRef = doc(db, 'teams', teamId, 'joinFormConfig', 'config');
        const [profileSnap, joinSnap] = await Promise.all([getDoc(profileRef), getDoc(joinRef)]);
        if (profileSnap.exists()) {
          const data = profileSnap.data();
          setEnabled(data.enabled !== false);
          const loadedFields = Array.isArray(data.fields) ? data.fields : [];
          setFields(loadedFields);
          initialFieldIdsRef.current = new Set(loadedFields.map(field => typeof field?.id === 'string' ? field.id.trim() : '').filter(Boolean));
        } else {
          setEnabled(true);
          setFields([]);
          initialFieldIdsRef.current = new Set();
        }
        const joinData = joinSnap.exists() ? joinSnap.data() || {} : {};
        setJoinQuestions(Array.isArray(joinData.questions) ? joinData.questions : []);
        confirmedJoinRemovalIdsRef.current = new Set();
      } catch (err) {
        setError('Failed to load profile fields.');
      } finally {
        setLoading(false);
      }
    }
    fetchConfig();
  }, [teamId, isOpen, getDoc]);
  const handleAddField = () => {
    setFields([...fields, {
      ...DEFAULT_FIELD,
      id: crypto.randomUUID()
    }]);
  };
  const handleUpdateField = (index, key, value) => {
    const updated = [...fields];
    updated[index] = {
      ...updated[index],
      [key]: value
    };
    setFields(updated);
  };
  const handleRemoveField = index => {
    const fieldToRemove = fields[index];
    const fieldId = typeof fieldToRemove?.id === 'string' ? fieldToRemove.id.trim() : '';
    const isUsedInJoinForm = fieldId ? joinQuestions.some(question => question?.id === fieldId) : false;
    const confirmRemove = window.confirm(
      isUsedInJoinForm
        ? `Remove "${fieldToRemove?.label || 'this profile field'}" from member profiles and the join form when you save?`
        : `Remove "${fieldToRemove?.label || 'this profile field'}" when you save?`
    );
    if (!confirmRemove) return;
    if (isUsedInJoinForm) {
      confirmedJoinRemovalIdsRef.current.add(fieldId);
    }
    const updated = [...fields];
    updated.splice(index, 1);
    setFields(updated);
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const cleaned = fields.map(field => {
        const label = typeof field.label === 'string' ? field.label.trim() : '';
        const type = FIELD_TYPES.includes(field.type) ? field.type : 'text';
        const options = Array.isArray(field.options) ? field.options : [];
        const normalizedOptions = options.map(opt => String(opt).trim()).filter(Boolean);
        if (!label || !field.id) return null;
        if (type === 'select' && normalizedOptions.length === 0) return null;
        return {
          id: field.id,
          label,
          type,
          required: !!field.required,
          options: type === 'select' ? normalizedOptions : [],
          ...(type === 'qualification' ? { evidenceRequired: field.evidenceRequired !== false, expires: field.expires === true, approvalRequired: false } : {})
        };
      }).filter(Boolean);
      const cleanedIds = new Set(cleaned.map(field => typeof field?.id === 'string' ? field.id.trim() : '').filter(Boolean));
      const removedProfileFieldIds = Array.from(initialFieldIdsRef.current).filter(fieldId => !cleanedIds.has(fieldId));
      const qualificationIds = cleaned.filter(field => field.type === 'qualification').map(field => field.id);
      const removedJoinLinkedIds = [...new Set([...removedProfileFieldIds, ...qualificationIds])].filter(fieldId => joinQuestions.some(question => question?.id === fieldId));
      const pendingConfirmIds = removedJoinLinkedIds.filter(fieldId => !confirmedJoinRemovalIdsRef.current.has(fieldId));
      if (pendingConfirmIds.length > 0) {
        const confirmRemove = window.confirm('This will remove this from the join form. Continue?');
        if (!confirmRemove) {
          setSaving(false);
          return;
        }
        pendingConfirmIds.forEach(fieldId => confirmedJoinRemovalIdsRef.current.add(fieldId));
      }
      await setDoc(doc(db, 'teams', teamId, 'profileFields', 'config'), {
        enabled: enabled && cleaned.length > 0,
        fields: cleaned
      });
      if (removedJoinLinkedIds.length > 0) {
        const nextQuestions = joinQuestions.filter(question => !removedJoinLinkedIds.includes(question?.id));
        await setDoc(doc(db, 'teams', teamId, 'joinFormConfig', 'config'), {
          questions: nextQuestions
        }, {
          merge: true
        });
      }
      if (onSave) await onSave();
      onClose();
    } catch (err) {
      setError('Failed to save profile fields.');
    } finally {
      setSaving(false);
    }
  };
  if (!isOpen) return null;
  return <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>Edit Profile Fields</h2>
        {error && <ErrorMessage message={error} />}
        {loading ? <p>Loading...</p> : <form onSubmit={handleSubmit}>
            <label>
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} disabled={fields.length === 0} />{' '}
              Enable profile fields
            </label>

            {fields.map((field, index) => <div key={field.id} className="form-question-editor">
                <TextInput label="Field label" value={field.label} onChange={e => handleUpdateField(index, 'label', e.target.value)} />

                <div className="field-row">
                  <div>
                    <label>Type</label>
                    <select value={field.type} onChange={e => handleUpdateField(index, 'type', e.target.value)}>
                      <option value="text">Short Text</option>
                      <option value="number">Number</option>
                      <option value="date">Date</option>
                      <option value="select">Select</option>
                      <option value="qualification">Qualification</option>
                    </select>
                  </div>

                  <div>
                    <label>
                      <input type="checkbox" checked={!!field.required} onChange={e => handleUpdateField(index, 'required', e.target.checked)} />{' '}
                      Required
                    </label>
                  </div>
                </div>

                {field.type === 'qualification' && <div className="qualification-editor">
                  <p>The field label is the qualification name. Members add their certificate and dates in their profile.</p>
                  <label className="qualification-checkbox"><input type="checkbox" checked={field.evidenceRequired !== false} onChange={e => handleUpdateField(index, 'evidenceRequired', e.target.checked)} />Require certificate evidence</label>
                  <label className="qualification-checkbox"><input type="checkbox" checked={field.expires === true} onChange={e => handleUpdateField(index, 'expires', e.target.checked)} />Has an expiry date</label>
                  <small>Uploads are accepted automatically. Evidence is private to the member and authorised admins.</small>
                </div>}

                {field.type === 'select' && <TextInput label="Options (comma separated)" value={(field.options || []).join(', ')} onChange={e => handleUpdateField(index, 'options', e.target.value.split(',').map(opt => opt.trim()))} />}

                <button type="button" className="remove-btn" onClick={() => handleRemoveField(index)}>
                  Remove This Field
                </button>
              </div>)}

            <button type="button" className="add-question-btn" onClick={handleAddField}>
              Add a New Field
            </button>

            <div className="form-actions" style={{
          marginTop: '1.5rem',
          display: 'flex',
          gap: '1rem'
        }}>
              <SubmitButton type="button" onClick={onClose} label="Cancel" variant="ghost" size="sm" />
              <SubmitButton type="submit" label={saving ? 'Saving...' : 'Save Profile Fields'} disabled={saving} variant="primary" />
            </div>
          </form>}
      </div>
    </div>;
}
